import React, { useState, useEffect } from 'react';
import { ErrorBanner } from '../../lib/ui/Feedback';
import Button from '../../lib/ui/Button';
import { buildUpiPayUrl } from '../../lib/format';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined'
    ? /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    : false));
  useEffect(() => {
    if (typeof window !== 'undefined') setIsMobile(/Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
  }, []);
  return isMobile;
}

export default function UpiStep({
  orderResult, total, upiSettings, copied, te, L,
  loading, error,
  handleCopyUpi, handleUpiDone, handleUpiBack,
}) {
  const isMobile = useIsMobile();
  const [deepLinkError, setDeepLinkError] = useState(false);

  const amt = Number(orderResult?.amount_due ?? orderResult?.total_amount ?? total).toFixed(2);
  const pa = upiSettings.upi_pa;
  const pn = upiSettings.upi_name;
  const tr = `NRK${Date.now()}`.slice(0, 20);
  const upiUrl = buildUpiPayUrl({ pa, pn, amount: amt, note: 'NRKMart', tr });
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(upiUrl)}&format=png&margin=12&color=1a3d16`;

  const handleOpenUpiApp = () => {
    try {
      window.location.href = upiUrl;
      setTimeout(() => setDeepLinkError(true), 2000);
    } catch {
      setDeepLinkError(true);
    }
  };

  return (
    <div>
      <div className="sticky top-14 md:top-[76px] z-20 -mx-4 md:-mx-6 px-4 md:px-6 py-3.5 mb-4 flex items-center justify-between bg-paper/95 backdrop-blur border-b border-black/5 shadow-soft">
        <button type="button" onClick={handleUpiBack} className="text-brand font-extrabold text-[13px]">← {L('Back', 'వెనక్కి')}</button>
        <div className="font-black text-[16px] text-brand-dark">📱 {L('Pay with UPI', 'UPIతో చెల్లించండి')}</div>
        <div className="w-12" />
      </div>

      <div className="bg-sky-50 border-[1.5px] border-sky-200 rounded-2xl px-4 py-3 mb-4">
        <div className="font-extrabold text-sm text-sky-800">{L('Pay now — order is saved only after you confirm.', 'ఇప్పుడు చెల్లించండి — నిర్ధారించిన తర్వాతే ఆర్డర్ సేవ్ అవుతుంది.')}</div>
        <div className="text-[13px] text-sky-700 mt-1 font-bold">{L('Amount', 'మొత్తం')}: ₹{amt}</div>
      </div>

      {isMobile ? (
        <div className="mb-4">
          <Button variant="accent" fullWidth onClick={handleOpenUpiApp}>
            📱 {L('Open GPay / PhonePe / Paytm', 'GPay / PhonePe / Paytm తెరవండి')}
          </Button>
          {deepLinkError ? (
            <div className="text-xs text-amber-600 mt-2 text-center">
              {L('Could not open UPI app. Please scan QR or copy UPI ID below.', 'UPI యాప్ తెరవలేకపోయాం. దయచేసి QR స్కాన్ చేయండి లేదా UPI ID కాపీ చేయండి.')}
            </div>
          ) : null}
          <div className="text-center text-ink-faint text-xs font-bold my-3">— {L('or', 'లేదా')} —</div>
        </div>
      ) : null}

      <div className="bg-brand-light/40 border-[1.5px] border-brand-light rounded-2xl px-4 py-5 mb-3.5 text-center">
        <div className="font-extrabold text-[15px] text-brand-dark mb-1">{L('Scan QR Code', 'QR కోడ్ స్కాన్ చేయండి')}</div>
        <div className="inline-block bg-white p-3.5 rounded-2xl border-2 border-brand shadow-card mt-2.5">
          <img src={qrUrl} alt="UPI QR Code" width={220} height={220} className="block" />
        </div>
      </div>

      <div className="bg-white border-[1.5px] border-black/10 rounded-2xl p-4 mb-4">
        <div className="font-extrabold text-[15px] text-brand-dark mb-3">{L('Or pay using UPI ID', 'లేదా UPI IDతో చెల్లించండి')}</div>
        <div className="text-[13px] font-bold text-ink-soft mb-2">{pn}</div>
        <div className="flex items-center gap-2.5">
          <div className="flex-1 bg-brand-light/40 border-[1.5px] border-black/10 rounded-xl px-3.5 py-3 font-black text-[16px] text-brand-dark font-mono break-all">{pa}</div>
          <button
            type="button"
            onClick={handleCopyUpi}
            className={`shrink-0 px-4 py-3 rounded-xl text-white font-extrabold text-[13px] ${copied ? 'bg-brand' : 'bg-brand-dark'}`}
          >
            {copied ? '✅ Copied!' : '📋 Copy'}
          </button>
        </div>
      </div>

      <ErrorBanner>{error}</ErrorBanner>

      <Button variant="accent" fullWidth size="lg" loading={loading} onClick={handleUpiDone}>
        {loading ? '…' : `✅ ${L("I've paid — place order", 'చెల్లించాను — ఆర్డర్ చేయి')}`}
      </Button>
      <div className="text-center mt-2 text-xs text-ink-faint leading-relaxed">
        {L('Going back does not create an order.', 'వెనక్కి వెళితే ఆర్డర్ సృష్టి కాదు.')}
      </div>
    </div>
  );
}
